<script lang="ts">
import Datepicker from '@vuepic/vue-datepicker';
import dayjs from 'dayjs';
import {
  computed,
  defineAsyncComponent,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  reactive,
  ref,
} from 'vue';

import { FutureDeliveryDates } from '@/api/shippingCalculator';
import Modal from '@/components/base/layout/Modal.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import money from '@/filters/money';
import { DateString } from '@/utils/dateTime';

const MonthYear = defineAsyncComponent(() => import('./Calendar/MonthYearHeader.vue'));

export default defineComponent({
  name: 'DeliveryDateCalendar',
  props: {
    futureDeliveryDates: {
      required: false,
      type: Object as PropType<FutureDeliveryDates>,
    },
    isOpen: { required: true, type: Boolean },
    loadingShippingOffers: { required: true, type: Boolean, default: false },
    modelValue: { required: false, type: String as unknown as PropType<DateString> },
  },
  components: {
    Datepicker,
    Modal,
    ThemedButton,
  },
  emits: ['update:modelValue', 'close-calendar'],
  setup(props, { emit }) {
    const calendarElement = ref<InstanceType<typeof Datepicker>>();
    const calendarWrapper = ref<InstanceType<typeof HTMLDivElement>>();
    const date = computed({
      get: () => (props.modelValue ? dayjs(props.modelValue).toDate() : undefined),
      set: (deliveryDate) =>
        emit('update:modelValue', deliveryDate ? DateString(deliveryDate) : undefined),
    });

    let windowWidth;
    onMounted(async () => {
      if (typeof window !== 'undefined') {
        windowWidth = window.innerWidth;
        const calendarWidth = calendarWrapper.value?.offsetWidth;
        await nextTick();
        if (windowWidth < 450 && calendarWrapper) {
          calendarWrapper.value!.style.transform = `scale(${
            windowWidth / (Number(calendarWidth) * 2)
          })`;
        }
      }
    });

    const handleClose = () => emit('close-calendar');

    const handleDone = () => {
      calendarElement.value!.selectDate();
      handleClose();
    };

    const resetDate = () => {
      calendarElement.value!.clearValue();
    };
    const customProps = reactive({
      resetDate,
    });

    const monthYear = computed(() => MonthYear);
    const allowedDates = computed(() => props.futureDeliveryDates?.dates.map((d) => d[0]));

    const formatDate = (dateSelected?: Date) => {
      if (!dateSelected) return '';

      const day = dateSelected.toLocaleString('default', { weekday: 'long' });
      const dateNumber = dateSelected.getDate();
      const month = dateSelected.toLocaleString('default', { month: 'long' });

      return `${day}, ${month} ${dateNumber}`;
    };

    const formatPrice = (dateToFormat: Date) => {
      if (allowedDates.value && props.futureDeliveryDates) {
        const currentDate = dayjs(dateToFormat).format('YYYY-MM-DD');
        if (!allowedDates.value.map((d) => String(d)).includes(currentDate)) return null;
        let price = props.futureDeliveryDates.defaultPrice;
        const [, currentDatePrice] =
          props.futureDeliveryDates.dates.find((d) => d[0] === currentDate) ?? [];
        if (currentDatePrice) price = currentDatePrice;
        return price.centAmount === 0 ? 'FREE' : money(price);
      }
      return '';
    };

    return {
      allowedDates,
      calendarElement,
      calendarWrapper,
      closeIcon: nutshell['img/close.svg'],
      customProps,
      date,
      formatDate,
      formatPrice,
      handleClose,
      handleDone,
      monthYear,
      resetDate,
    };
  },
});
</script>

<template>
  <Modal
    breakpoint="sm"
    data-test="delivery-date-calendar"
    :isOpen="isOpen"
    :hasControls="false"
    @handle-close="handleClose"
    aria-label="Delivery date calendar"
    width="sm:w-max"
  >
    <template #header>
      <div
        class="mb-4 text-lg font-medium font-sofia-pro md:text-2xl"
        data-test="delivery-date-calendar-header"
      >
        Choose a delivery date
      </div>
    </template>
    <template #body>
      <div ref="calendarWrapper" class="flex-col items-center">
        <div :class="{ 'opacity-50': loadingShippingOffers }">
          <Datepicker
            v-model="date"
            ref="calendarElement"
            data-test="delivery-date-calendar-date-picker"
            calendarClassName="dp-custom-calendar"
            calendarCellClassName="dp-custom-cell"
            :allowedDates="allowedDates"
            menuClassName="dp-custom-menu"
            :closeOnAutoApply="false"
            :customProps="customProps"
            :enableTimePicker="false"
            hideOffsetDates
            inline
            :monthChangeOnScroll="false"
            monthNameFormat="long"
            :month-year-component="monthYear"
            noToday
            weekStart="0"
          >
            <template #calendar-header="{ day }">
              <span class="font-normal text-neutral-600">{{ day[0] }}</span>
            </template>

            <template #day="{ day, date }">
              <div v-if="day" class="flex-col">
                <div :data-test="`delivery-date-calendar-day${day}`">{{ day }}</div>
                <div
                  class="text-xs text-neutral-600"
                  :data-test="`delivery-date-calendar-price${formatPrice(date)}`"
                >
                  {{ formatPrice(date) }}
                </div>
              </div>
            </template>

            <template #action-preview="{ value }">
              <p class="text-base text-left font-proxima-nova escape-legacy">
                {{ formatDate(value) }}
              </p>
            </template>
            <template #action-select />
          </Datepicker>
          <div class="flex items-center justify-end">
            <div
              class="mr-6 underline cursor-pointer font-proxima-nova underline-offset-2"
              @click="resetDate"
              data-test="delivery-date-calendar-reset"
              @keydown.enter.prevent="resetDate"
              @keydown.space.prevent="resetDate"
              role="button"
              tabindex="0"
            >
              Reset
            </div>
            <ThemedButton
              theme="gray"
              type="button"
              @click="handleDone"
              data-test="delivery-date-calendar-done"
            >
              Done
            </ThemedButton>
          </div>
        </div>
      </div>
      <img
        v-if="loadingShippingOffers"
        class="absolute inset-0 mx-auto my-auto w-14 h-14 animate-spin spinner"
        src="@/assets/spinner.svg"
        alt="loading"
      />
    </template>
  </Modal>
</template>

<style lang="scss">
@import '@vuepic/vue-datepicker/dist/main.css';

.spinner {
  filter: invert(1);
}

.dp__menu,
.dp__menu:focus {
  border: none;
}
.dp__main {
  border: 1px solid #e4e3e2;
  padding: 0.5rem;
  margin-bottom: 1.5rem;
}

.dp-custom-cell {
  border-radius: 50%;
  padding: 1.25rem;
  margin: 0.25rem;
}

.dp__date_hover_end,
.dp__date_hover_start,
.dp__date_hover,
.dp__date_hover_end:hover,
.dp__date_hover_start:hover,
.dp__date_hover:hover {
  background: none;
  border: 1px solid white;
}

.dp__range_end,
.dp__range_start,
.dp__active_date {
  background: #ffe171;
  color: black;
}

.dp__calendar_header_item {
  width: 50px;
}

.dp__calendar_header_separator {
  display: none;
}

.dp__selection_preview {
  width: 100%;
}

@media screen and (max-width: 768px) {
  .dp-custom-calendar {
    width: 100%;
  }
}
</style>
